import { auth, db } from "@/lib/firebase";
import { type FollowPrivacy } from "@/data/follow";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  approveFollow,
  removeFollowEdge,
  requestFollow,
  type FollowStatus,
} from "@/data/follow";
import {
  Share2,
  MessageSquare,
  ExternalLink,
  Check,
  X,
  UserMinus,
  UserPlus,
  Users as UsersIcon,
  MoreHorizontal,
} from "lucide-react";
import React, { useEffect, useMemo, useState, useRef } from "react";
import type { ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import RankBadge from "@/components/RankBadge";
import {
  collection,
  documentId,
  getDocs,
  onSnapshot,
  query,
  Unsubscribe,
  where,
  type DocumentData,
  type QueryDocumentSnapshot, // type-only to avoid runtime import
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { toast } from "@/components/ui/use-toast";
import { getCachedProfile, prefetchProfiles } from "@/lib/profileCache";
import FollowButton from "@/components/follow/FollowButton";

/* ---------------- Types ---------------- */
type MenuItemDef = { key: string; label: string; icon: ReactNode; onClick: () => void };

type FollowEdgeDoc = {
  followerId: string;
  followingId: string;
  status: FollowStatus;
  createdAt?: unknown;
  updatedAt?: unknown;
  _id: string; // doc id
};

type UserBasic = {
  uid: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  rank_title?: string | null;     // added
  rank?: number | null;           // added
};

/* -------------- Helpers -------------- */

function chunk<T>(arr: T[], size: number): T[][];
function chunk<T>(arr: T[], size: number) {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function unique<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}

/* -------------- Small UI helpers -------------- */

function SectionCard({
  title,
  count,
  children,
  rightSlot,
}: {
  title: string;
  count?: number;
  children: ReactNode;
  rightSlot?: ReactNode;
}) {
  return (
    <Card className="bg-gray-900/90 border-gray-800 shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-white flex items-center gap-2">
            <UsersIcon className="w-5 h-5 text-slate-300" />
            <span>{title}</span>
            {typeof count === "number" && (
              <span className="ml-1 rounded-full bg-white/10 text-white/90 text-xs px-2 py-0.5">
                {count}
              </span>
            )}
          </CardTitle>
          {rightSlot}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/* -------------- Component -------------- */

export default function Followers() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Auth state (reactive)
  const [uid, setUid] = useState<string | null>(auth.currentUser?.uid ?? null);
  const [authReady, setAuthReady] = useState<boolean>(!!auth.currentUser);

  // Page data
  const [incomingPending, setIncomingPending] = useState<FollowEdgeDoc[]>([]);
  const [outgoingPending, setOutgoingPending] = useState<FollowEdgeDoc[]>([]);
  const [following, setFollowing] = useState<FollowEdgeDoc[]>([]);
  const [followers, setFollowers] = useState<FollowEdgeDoc[]>([]);
  const [profiles, setProfiles] = useState<Record<string, UserBasic>>({});
  const [privacies, setPrivacies] = useState<Record<string, FollowPrivacy>>({});
  const [loading, setLoading] = useState<boolean>(true);
  // Track errors per subscription to avoid stale global error banners
  const [errIncoming, setErrIncoming] = useState<string | null>(null);
  const [errOutgoing, setErrOutgoing] = useState<string | null>(null);
  const [errFollowing, setErrFollowing] = useState<string | null>(null);
  const [errFollowers, setErrFollowers] = useState<string | null>(null);
  // inline row-menu state
  const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);
  // Search state
  const [searchText, setSearchText] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"followers" | "following" | "requests">(
    (searchParams.get("tab") as any) === "following"
      ? "following"
      : (searchParams.get("tab") as any) === "requests"
      ? "requests"
      : "followers"
  );

  // Subscribe to auth changes once
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUid(u?.uid ?? null);
      setAuthReady(true);
    });
    return unsub;
  }, []);

  // Subscribe to all 4 views for this user
  useEffect(() => {
    if (!authReady) return; // wait until auth state resolves
    if (!uid) {
      setIncomingPending([]);
      setOutgoingPending([]);
      setFollowing([]);
      setFollowers([]);
      setProfiles({});
      setLoading(false);
      setErrIncoming(null);
      setErrOutgoing(null);
      setErrFollowing(null);
      setErrFollowers(null);
      return;
    }

    setLoading(true);
    const col = collection(db, "follows");
    const unsubscribes: Unsubscribe[] = [];

    // Incoming requests (others -> me) pending
    unsubscribes.push(
      onSnapshot(
        query(col, where("followingId", "==", uid), where("status", "==", "pending")),
        (snap) => {
          setIncomingPending(snap.docs.map(mapEdgeDoc));
          setErrIncoming(null);
        },
        (e) => {
          console.error(e);
          setErrIncoming("Failed to load incoming requests");
        },
      ),
    );

    // Outgoing requests (me -> others) pending
    unsubscribes.push(
      onSnapshot(
        query(col, where("followerId", "==", uid), where("status", "==", "pending")),
        (snap) => {
          setOutgoingPending(snap.docs.map(mapEdgeDoc));
          setErrOutgoing(null);
        },
        (e) => {
          console.error(e);
          setErrOutgoing("Failed to load outgoing requests");
        },
      ),
    );

    // I follow (approved)
    unsubscribes.push(
      onSnapshot(
        query(col, where("followerId", "==", uid), where("status", "==", "approved")),
        (snap) => {
          setFollowing(snap.docs.map(mapEdgeDoc));
          setErrFollowing(null);
        },
        (e) => {
          console.error(e);
          setErrFollowing("Failed to load following");
        },
      ),
    );

    // Following me (approved)
    unsubscribes.push(
      onSnapshot(
        query(col, where("followingId", "==", uid), where("status", "==", "approved")),
        (snap) => {
          setFollowers(snap.docs.map(mapEdgeDoc));
          setErrFollowers(null);
        },
        (e) => {
          console.error(e);
          setErrFollowers("Failed to load followers");
        },
      ),
    );

    setLoading(false);
    return () => unsubscribes.forEach((u) => u());
  }, [authReady, uid]);

  // Load basic profiles for all UIDs we show
  useEffect(() => {
    (async () => {
      if (!uid) return;
      // Collect all other user ids
      const ids = unique([
        ...incomingPending.map((e) => e.followerId),
        ...outgoingPending.map((e) => e.followingId),
        ...following.map((e) => e.followingId),
        ...followers.map((e) => e.followerId),
      ]).filter(Boolean);

      // Prune those we already have
      const missing = ids.filter((id) => !profiles[id]);
      if (missing.length === 0) return;

      const next: Record<string, UserBasic> = {};
      const chunks = chunk(missing, 10);
      for (const group of chunks) {
        const snap = await getDocs(query(collection(db, "users"), where(documentId(), "in", group)));
        snap.forEach((docSnap) => {
          const v = docSnap.data() as {
            username?: string | null;
            display_name?: string | null;
            avatar_url?: string | null;
            rank_title?: string | null;   // added
            rank?: number | null;         // added
            follow_privacy?: string | null;
            preferences?: { follow_privacy?: string | null } | null;
          };
          next[docSnap.id] = {
            uid: docSnap.id,
            username: String(v.username || docSnap.id.slice(0, 6)),
            display_name: v.display_name ?? null,
            avatar_url: v.avatar_url ?? null,
            rank_title: v.rank_title ?? null, // added
            rank: v.rank ?? null,             // added
          };
          const raw = v.follow_privacy ?? v.preferences?.follow_privacy ?? null;
          setPrivacies((prev) => ({ ...prev, [docSnap.id]: (raw ? raw.toLowerCase() : "review") as FollowPrivacy }));
        });
      }
      setProfiles((prev) => ({ ...prev, ...next }));
    })().catch((e: unknown) => {
      console.error("Profiles load failed:", e);
    });
  }, [uid, incomingPending, outgoingPending, following, followers, profiles]);

  // Trigger re-render when prefetch completes (discard value to avoid TS unused warnings)
  const [, setProfileCacheTick] = useState(0);

  // Prefetch all counterpart profiles visible across tabs
  useEffect(() => {
    try {
      const ids = new Set<string>();
      for (const e of (followers || [])) ids.add(e.followerId);
      for (const e of (following || [])) ids.add(e.followingId);
      for (const e of (incomingPending || [])) ids.add(e.followerId);
      for (const e of (outgoingPending || [])) ids.add(e.followingId);
      const arr = Array.from(ids).filter(Boolean);
      if (!arr.length) return;
      void prefetchProfiles(arr).then(() => setProfileCacheTick((v) => v + 1));
    } catch {}
  }, [followers, following, incomingPending, outgoingPending]);

  /* ---------- Derived UI data (hooks must remain before any return) ---------- */
  // Normalize arrays once (prevents undefined access inside memos)
  const safeFollowers = Array.isArray(followers) ? followers : [];
  const safeFollowing = Array.isArray(following) ? following : [];
  const safeIncoming  = Array.isArray(incomingPending) ? incomingPending : [];
  const safeOutgoing  = Array.isArray(outgoingPending) ? outgoingPending : [];

  const search = (searchText ?? "").trim().toLowerCase();

  const { followersApproved, followingApproved, requestsIncoming, requestsOutgoing } = useMemo(() => {
    return {
      followersApproved: safeFollowers.filter((e) => e.status === "approved"),
      followingApproved: safeFollowing.filter((e) => e.status === "approved"),
      requestsIncoming:  safeIncoming.filter((e) => e.status === "pending"),
      requestsOutgoing:  safeOutgoing.filter((e) => e.status === "pending"),
    };
  }, [safeFollowers, safeFollowing, safeIncoming, safeOutgoing]);

  // Set of UIDs I follow (for mutual badge)
  const followingIds = useMemo(() => new Set(safeFollowing.map((e) => e.followingId)), [safeFollowing]);

  const filteredFollowers = useMemo(() => {
    if (!search) return followersApproved;
    return followersApproved.filter((e: any) =>
      ((e._profile?.username || e._profile?.display_name || "") as string).toLowerCase().includes(search),
    );
  }, [followersApproved, search]);

  const filteredFollowing = useMemo(() => {
    if (!search) return followingApproved;
    return followingApproved.filter((e: any) =>
      ((e._profile?.username || e._profile?.display_name || "") as string).toLowerCase().includes(search),
    );
  }, [followingApproved, search]);

  const filteredIncoming = useMemo(() => {
    if (!search) return requestsIncoming;
    return requestsIncoming.filter((e: any) =>
      ((e._profile?.username || e._profile?.display_name || "") as string).toLowerCase().includes(search),
    );
  }, [requestsIncoming, search]);

  const filteredOutgoing = useMemo(() => {
    if (!search) return requestsOutgoing;
    return requestsOutgoing.filter((e: any) =>
      ((e._profile?.username || e._profile?.display_name || "") as string).toLowerCase().includes(search),
    );
  }, [requestsOutgoing, search]);

  // Tabs and counts (must be before any return)
  const counts = useMemo(
    () => ({
      incoming: incomingPending.length,
      outgoing: outgoingPending.length,
      following: following.length,
      followers: followers.length,
    }),
    [incomingPending, outgoingPending, following, followers],
  );
  const tabDefs = useMemo(
    () => [
      { key: "followers" as const, label: "followers", count: counts.followers },
      { key: "following" as const, label: "following", count: counts.following },
      { key: "requests"  as const, label: "requests",  count: counts.incoming + counts.outgoing },
    ],
    [counts],
  );

  // Derive a single banner message (before returns)
  const displayErr = useMemo(() => {
    if (errFollowing && following.length === 0) return errFollowing;
    if (errFollowers && followers.length === 0) return errFollowers;
    if (errIncoming && incomingPending.length === 0) return errIncoming;
    if (errOutgoing && outgoingPending.length === 0) return errOutgoing;
    return null;
  }, [
    errFollowing,
    errFollowers,
    errIncoming,
    errOutgoing,
    following.length,
    followers.length,
    incomingPending.length,
    outgoingPending.length,
  ]);

  // Only return after all hooks above have executed
  if (!uid) {
    return (
      <main className="max-w-4xl mx-auto p-6">
        <Card className="bg-gray-900 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Followers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-slate-300">Please sign in to manage followers.</div>
          </CardContent>
        </Card>
      </main>
    );
  }

  // ----- Row menu builders (no hooks) -----
  async function copyProfileLink(uname: string, userId: string) {
    const href = `${window.location.origin}/${uname || userId}`;
    try {
      await (navigator.clipboard?.writeText(href) ?? Promise.reject());
      toast({ description: "Profile link copied" });
    } catch {
      try {
        // Fallback
        const ta = document.createElement("textarea");
        ta.value = href;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast({ description: "Profile link copied" });
      } catch {
        toast({ description: href });
      }
    }
  }

  const menuForFollowing = (e: FollowEdgeDoc, uname: string): MenuItemDef[] => [
    {
      key: "msg",
      label: "Message",
      icon: <MessageSquare className="w-4 h-4" />,
      onClick: () => navigate(`/inbox?tab=messages&to=${e.followingId}`),
    },
    {
      key: "share",
      label: "Share profile",
      icon: <Share2 className="w-4 h-4" />,
      onClick: () => void copyProfileLink(uname, e.followingId),
    },
    {
      key: "view",
      label: "View profile",
      icon: <ExternalLink className="w-4 h-4" />,
      onClick: () => navigate(`/${uname || e.followingId}`),
    },
    {
      key: "unfollow",
      label: "Unfollow",
      icon: <UserMinus className="w-4 h-4" />,
      onClick: async () => {
        try {
          await removeFollowEdge(e._id);
          toast({ description: "Unfollowed" });
        } catch (err) {
          console.error(err);
          toast({ description: "Failed to unfollow" });
        }
      },
    },
  ];

  const menuForFollowers = (e: FollowEdgeDoc, uname: string, isMutual: boolean): MenuItemDef[] => [
    {
      key: "msg",
      label: "Message",
      icon: <MessageSquare className="w-4 h-4" />,
      onClick: () => navigate(`/inbox?tab=messages&to=${e.followerId}`),
    },
    {
      key: "share",
      label: "Share profile",
      icon: <Share2 className="w-4 h-4" />,
      onClick: () => void copyProfileLink(uname, e.followerId),
    },
    {
      key: "view",
      label: "View profile",
      icon: <ExternalLink className="w-4 h-4" />,
      onClick: () => navigate(`/${uname || e.followerId}`),
    },
    isMutual
      ? {
          key: "removeFollower",
          label: "Remove follower",
          icon: <UserMinus className="w-4 h-4" />,
          onClick: async () => {
            try {
              await removeFollowEdge(e._id);
              toast({ description: "Removed follower" });
            } catch (err) {
              console.error(err);
              toast({ description: "Failed to remove follower" });
            }
          },
        }
      : privacies[e.followerId] === "private"
      ? null
      : {
          key: "followBack",
          label: "Follow back",
          icon: <UserPlus className="w-4 h-4" />,
          onClick: async () => {
            try {
              await requestFollow(e.followerId);
              toast({ description: "Followed" });
            } catch (err: any) {
              console.error(err);
              const msg = String(err?.message || "").toLowerCase();
              toast({
                description: msg.includes("private")
                  ? "This user’s profile is private."
                  : "Failed to follow",
              });
            }
          },
        },
  ].filter(Boolean) as MenuItemDef[];

  const menuForIncoming = (e: FollowEdgeDoc, uname: string): MenuItemDef[] => [
    {
      key: "approve",
      label: "Approve",
      icon: <Check className="w-4 h-4" />,
      onClick: async () => {
        try {
          await approveFollow(e._id);
          toast({ description: "Request approved" });
        } catch (err) {
          console.error(err);
          toast({ description: "Failed to approve request" });
        }
      },
    },
    {
      key: "decline",
      label: "Decline",
      icon: <X className="w-4 h-4" />,
      onClick: async () => {
        try {
          await removeFollowEdge(e._id);
          toast({ description: "Request declined" });
        } catch (err) {
          console.error(err);
          toast({ description: "Failed to decline request" });
        }
      },
    },
    {
      key: "view",
      label: "View profile",
      icon: <ExternalLink className="w-4 h-4" />,
      onClick: () => navigate(`/${uname || e.followerId}`),
    },
  ];

  const menuForOutgoing = (e: FollowEdgeDoc, uname: string): MenuItemDef[] => [
    {
      key: "cancel",
      label: "Cancel request",
      icon: <X className="w-4 h-4" />,
      onClick: async () => {
        try {
          await removeFollowEdge(e._id);
          toast({ description: "Request canceled" });
        } catch (err) {
          console.error(err);
          toast({ description: "Failed to cancel request" });
        }
      },
    },
    {
      key: "view",
      label: "View profile",
      icon: <ExternalLink className="w-4 h-4" />,
      onClick: () => navigate(`/${uname || e.followingId}`),
    },
  ];

  // Reusable row that matches profile/list aesthetics
  function ProfileRow({
    user,
    cta,
  }: {
    user: { uid: string; username?: string | null; display_name?: string | null; avatar_url?: string | null; bio?: string | null };
    cta?: React.ReactNode;
  }) {
    const uname = user.username || user.display_name || user.uid.slice(0, 6);
    return (
      <div className="flex items-center justify-between gap-3 p-2 rounded-lg border border-slate-800 bg-black/30 hover:bg-white/5">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={user.avatar_url || "/images/default-avatar.png"}
            alt={uname}
            className="h-12 w-12 rounded-full object-cover border border-slate-700"
            loading="lazy"
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-white font-medium truncate">@{uname}</span>
            </div>
            {user.bio && <div className="text-xs text-slate-400 truncate">{user.bio}</div>}
          </div>
        </div>
        <div className="shrink-0">{cta}</div>
      </div>
    );
  }

  // choose list to render based on active tab
  let listRows: React.ReactNode = null;
  if (activeTab === "following") {
    listRows =
      following.length === 0 ? (
        <div className="text-slate-400 text-sm">You don’t follow anyone yet.</div>
      ) : (
        <div className="space-y-2">
          {following.map((e) => {
            const uname = profiles[e.followingId]?.username || e.followingId;
            return (
              <Row
                key={e._id}
                uid={e.followingId}
                items={menuForFollowing(e, uname)}
                isOpen={openMenuFor === e.followingId}
                onToggle={(id) => setOpenMenuFor((cur) => (cur === id ? null : id))}
                onClose={() => setOpenMenuFor(null)}
              />
            );
          })}
        </div>
      );
  } else if (activeTab === "followers") {
    listRows =
      followers.length === 0 ? (
        <div className="text-slate-400 text-sm">No followers yet.</div>
      ) : (
        <div className="space-y-3">
          {followers.map((e) => {
            const uid = e.followerId;
            const uname = profiles[uid]?.username || uid;
            const isMutual = followingIds.has(uid);
            return (
              <Row
                key={e._id}
                uid={uid}
                badge={
                  isMutual ? (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                      Mutual
                    </span>
                  ) : null
                }
                items={menuForFollowers(e, uname, isMutual)}
                isOpen={openMenuFor === uid}
                onToggle={(id) => setOpenMenuFor((cur) => (cur === id ? null : id))}
                onClose={() => setOpenMenuFor(null)}
              />
            );
          })}
        </div>
      );
  } else {
    // requests tab (incoming + outgoing)
    const none = incomingPending.length === 0 && outgoingPending.length === 0;
    listRows = none ? (
      <div className="text-slate-400 text-sm">No pending requests.</div>
    ) : (
      <div className="space-y-4">
        {incomingPending.length > 0 && (
          <SectionCard title="Incoming requests" count={incomingPending.length}>
            <div className="space-y-3 mt-2">
              {incomingPending.map((e) => {
                const uname = profiles[e.followerId]?.username || e.followerId;
                return (
                  <Row
                    key={e._id}
                    uid={e.followerId}
                    items={menuForIncoming(e, uname)}
                    isOpen={openMenuFor === e.followerId}
                    onToggle={(id) => setOpenMenuFor((cur) => (cur === id ? null : id))}
                    onClose={() => setOpenMenuFor(null)}
                  />
                );
              })}
            </div>
          </SectionCard>
        )}
        {outgoingPending.length > 0 && (
          <SectionCard title="Outgoing requests" count={outgoingPending.length}>
            <div className="space-y-3 mt-2">
              {outgoingPending.map((e) => {
                const uname = profiles[e.followingId]?.username || e.followingId;
                return (
                  <Row
                    key={e._id}
                    uid={e.followingId}
                    items={menuForOutgoing(e, uname)}
                    isOpen={openMenuFor === e.followingId}
                    onToggle={(id) => setOpenMenuFor((cur) => (cur === id ? null : id))}
                    onClose={() => setOpenMenuFor(null)}
                  />
                );
              })}
            </div>
          </SectionCard>
        )}
      </div>
    );
  }

  // Keep URL in sync when tab changes
  useEffect(() => {
    const cur = searchParams.get("tab");
    if (cur !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", activeTab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <div className="px-3 sm:px-4 py-3">
      <header className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white">Followers &amp; Following</h1>
          <div className="text-slate-400 text-sm mt-1">{loading ? "Syncing…" : `${counts.followers} followers • ${counts.following} following`}</div>
        </div>

        {/* Tabs */}
        <nav className="flex gap-2" aria-label="Followers tabs">
          {tabDefs.map((t) => {
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`px-3 py-2 rounded-lg font-medium text-sm sm:text-base ${active ? "bg-fuchsia-600/25 text-white border border-fuchsia-500/40" : "bg-black/30 text-white/70 border border-slate-800"} focus:outline-none`}
              >
                <span className="capitalize">{t.label}</span>
                <span className="ml-2 inline-block rounded-full bg-white/10 text-white/90 text-xs px-2 py-0.5">{t.count}</span>
              </button>
            );
          })}
        </nav>
      </header>

      {displayErr && (
        <div className="rounded-lg border border-red-700 bg-red-900/30 text-red-200 px-3 py-2">
          {displayErr}
        </div>
      )}

      <section className="space-y-4">
        {/* Search box (always visible, even when no results) */}
        <div className="relative">
          <input
            value={searchText ?? ""}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search followers..."
            className="block w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-fuchsia-500 focus:ring-1 focus:ring-fuchsia-500/20"
            aria-label="Search followers"
          />
        </div>

        {listRows}
      </section>
    </div>
  );
}

/* ---------- Row component used in lists ---------- */
function Row({
  uid,
  items,
  badge,
  isOpen,
  onToggle,
  onClose,
}: {
  uid: string;
  items: MenuItemDef[];
  badge?: ReactNode;
  isOpen: boolean;
  onToggle: (id: string) => void;
  onClose: () => void;
}) {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(t) &&
        anchorRef.current &&
        !anchorRef.current.contains(t)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isOpen, onClose]);

  const p = getCachedProfile(uid);
  const username =
    p?.username || p?.display_name || p?.displayName || uid.slice(0, 12);
  const img = p?.avatar_url || p?.photoURL || "/images/avatars/default.png";

  // If the badge is a RankBadge, force it to 50% of screen width
  const renderedBadge =
    React.isValidElement(badge) && (badge as any).type === RankBadge ? (
      <div className="w-[50vw] max-w-full overflow-hidden">{badge}</div>
    ) : (
      badge
    );

  return (
    <div className="w-full rounded-lg border border-gray-800 bg-gray-900/40 px-3 py-2">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-700 bg-black/30 shrink-0">
          <img src={img} alt={username} className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <a
              href={`/${username}`}
              className="text-white font-semibold truncate hover:underline"
              title={`@${username}`}
            >
              @{username}
            </a>
            {renderedBadge}
          </div>
        </div>
        <button
          ref={anchorRef}
          className="ml-auto p-1.5 rounded-md border border-gray-700 text-slate-200 hover:bg-white/5"
          onClick={() => onToggle(uid)}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-label="Open actions"
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </div>

      {isOpen && (
        <div
          ref={menuRef}
          className="mt-2 rounded-lg border border-gray-700 bg-black/90 p-1 shadow-xl"
          role="menu"
        >
          {items.map((m) => (
            <button
              key={m.key}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-slate-200 hover:bg-white/5"
              onClick={() => {
                try {
                  m.onClick();
                } finally {
                  onClose();
                }
              }}
              role="menuitem"
            >
              <span className="w-4 h-4 shrink-0">{m.icon}</span>
              <span>{m.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Helpers (reusable) ---------- */
function mapEdgeDoc(doc: QueryDocumentSnapshot<DocumentData>): FollowEdgeDoc {
  const d = doc.data() as DocumentData;
  return {
    followerId: String(d.followerId || ""),
    followingId: String(d.followingId || ""),
    status: (d.status as FollowStatus) || "pending",
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
    _id: doc.id,
  };
}

